import type { EntitySchema } from './types';

// Common genres
const GENRES = [
  { value: 'Action', label: 'Action' },
  { value: 'Adventure', label: 'Adventure' },
  { value: 'Comedy', label: 'Comedy' },
  { value: 'Drama', label: 'Drama' },
  { value: 'Horror', label: 'Horror' },
  { value: 'Romance', label: 'Romance' },
  { value: 'Thriller', label: 'Thriller' },
  { value: 'Science Fiction', label: 'Science Fiction' },
  { value: 'Fantasy', label: 'Fantasy' },
  { value: 'Animation', label: 'Animation' },
  { value: 'Documentary', label: 'Documentary' },
  { value: 'Crime', label: 'Crime' },
  { value: 'Mystery', label: 'Mystery' },
  { value: 'Musical', label: 'Musical' },
  { value: 'War', label: 'War' },
  { value: 'Western', label: 'Western' },
  { value: 'Family', label: 'Family' },
  { value: 'Biography', label: 'Biography' },
  { value: 'History', label: 'History' },
  { value: 'Sport', label: 'Sport' },
];

// Common languages
const LANGUAGES = [
  { value: 'Tamil', label: 'Tamil' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Telugu', label: 'Telugu' },
  { value: 'Malayalam', label: 'Malayalam' },
  { value: 'Kannada', label: 'Kannada' },
  { value: 'English', label: 'English' },
];

// Certifications
const CERTIFICATIONS = [
  { value: 'U', label: 'U (Universal)' },
  { value: 'U/A', label: 'U/A (Parental Guidance)' },
  { value: 'A', label: 'A (Adults Only)' },
  { value: 'S', label: 'S (Restricted)' },
];

export const movieSchema: EntitySchema = {
  entityType: 'movie',
  displayName: 'Movie',
  description: 'Movies with box office, cast, and crew information',
  categories: [
    {
      name: 'Basic Information',
      description: 'Release date, runtime, and ratings',
      fields: [
        {
          path: 'basic.releaseDate',
          label: 'Release Date',
          type: 'date',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'YYYY-MM-DD',
          description: 'Movie release date',
        },
        {
          path: 'basic.runtime',
          label: 'Runtime (minutes)',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 120',
          description: 'Movie runtime in minutes',
        },
        {
          path: 'basic.language',
          label: 'Language',
          type: 'array',
          operators: ['contains'],
          options: LANGUAGES,
          description: 'Movie language(s)',
        },
        {
          path: 'basic.certification',
          label: 'Certification',
          type: 'string',
          operators: ['eq'],
          options: CERTIFICATIONS,
          description: 'CBFC certification',
        },
      ],
    },
    {
      name: 'Box Office',
      description: 'Financial performance',
      fields: [
        {
          path: 'financial.boxOffice.worldwide',
          label: 'Worldwide Box Office',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 1000000000',
          description: 'Worldwide box office collection',
        },
        {
          path: 'financial.boxOffice.domestic',
          label: 'Domestic Box Office',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 500000000',
          description: 'Domestic box office collection',
        },
        {
          path: 'financial.budget',
          label: 'Budget',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 100000000',
          description: 'Production budget',
        },
      ],
    },
    {
      name: 'People',
      description: 'Cast and crew',
      fields: [
        {
          path: 'people.directors',
          label: 'Director',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Movie director(s)',
        },
        {
          path: 'people.cast',
          label: 'Actor/Actress',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Cast members',
        },
        {
          path: 'people.producers',
          label: 'Producer',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Movie producer(s)',
        },
        {
          path: 'people.musicDirectors',
          label: 'Music Director',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Music director/composer',
        },
        {
          path: 'people.cinematographers',
          label: 'Cinematographer',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Director of photography',
        },
      ],
    },
    {
      name: 'Categories',
      description: 'Genres, studios, and platforms',
      fields: [
        {
          path: 'categories.genres',
          label: 'Genre',
          type: 'array',
          operators: ['contains'],
          options: GENRES,
          description: 'Movie genre(s)',
        },
        {
          path: 'categories.studios',
          label: 'Studio',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Production studio(s)',
        },
        {
          path: 'categories.streamingPlatforms',
          label: 'Streaming Platform',
          type: 'array',
          operators: ['contains'],
          options: [
            { value: 'Netflix', label: 'Netflix' },
            { value: 'Amazon Prime Video', label: 'Amazon Prime Video' },
            { value: 'Disney+ Hotstar', label: 'Disney+ Hotstar' },
            { value: 'SonyLIV', label: 'SonyLIV' },
            { value: 'ZEE5', label: 'ZEE5' },
            { value: 'Aha', label: 'Aha' },
            { value: 'Sun NXT', label: 'Sun NXT' },
          ],
          description: 'Available streaming platforms',
        },
      ],
    },
  ],
};
